// prepareFAQPageMeta.js
import getListData from '../selector/getListData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import generateFAQPageJsonLd from '../resource/generateFAQPageJsonLd.js';
import { BRANDING_NAME } from '../RemoteConfigKeys.js';
import getIntercomArticleDataList from '../selector/getIntercomArticleDataList.js';

/**
 * Prepare meta data for FAQ page
 * @param {Object} options
 * @param {Object} options.state - Redux state
 * @param {string} options.language - Current language
 * @param {string} options.hostname - Request hostname
 * @param {boolean} options.includeJsonLd - Whether to include JSON-LD data
 * @param {string} options.canonical - Canonical URL (optional)
 * @param {Array} options.alternates - Alternate language links (optional)
 * @param {Object} options.i18n - i18n instance
 * @param {Array} options.supportedLanguages - Supported languages
 * @returns {Promise<Array>} Meta tags array for Helmet
 */
const prepareFAQPageMeta = async ({
  state,
  language = 'en',
  hostname = 'swag.live',
  includeJsonLd = false,
  canonical = null,
  alternates = null,
  i18n,
  supportedLanguages,
}) => {
  // Get branding name from remote config
  const brandingName = getRemoteConfigData(state, BRANDING_NAME);

  const { t } = i18n;

  // Get FAQ title and description from translations
  const title = t('seo_faq_title', { ns: TranslationNamespace.SEO });
  const description = t('seo_faq_description', {
    ns: TranslationNamespace.SEO,
  });
  // Prepare meta options
  const metaOptions = {
    title,
    description,
    images: [],
    ogType: 'website',
    twitterCard: 'summary',
    hostname,
    url: canonical,
    siteName: brandingName,
    alternates: {
      canonical,
      alternates,
    },
  };

  // Add JSON-LD if requested
  if (includeJsonLd) {
    // Get FAQ articles from state
    const listSelectPath = ['faq', language];
    const articleIds = getListData(state, listSelectPath, 'itemIds') || [];

    // filter falsy items eg. null
    const faqItems = getIntercomArticleDataList(
      state,
      language,
      articleIds
    ).filter(Boolean);

    const jsonLdData = await generateFAQPageJsonLd({
      faqItems,
      pageUrl: canonical || `https://${hostname}/settings/faq`,
      supportedLanguages,
      origin: `https://${hostname}`,
      i18n,
    });

    metaOptions.jsonLd = jsonLdData;
  }

  return getMetaTagsForHelmet(metaOptions);
};

export default prepareFAQPageMeta;
